import { useCallback, useState } from 'react';
import { Alert, Box, Button, Group, Space, Text, TextInput } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { useInputState } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { postExternalApiKeyCreateApiKey } from '@apis/Customers';
import { ApiAccessKeyResult } from '@apis/Customers/model';

export function ApiAccessKeyModal(props: { onClose: (didCreate: boolean, result?: ApiAccessKeyResult) => void }) {
    const [errorMessage, setErrorMessage] = useState<JSX.Element>(<Text></Text>);
    const [error, setError] = useInputState(false);
    const [submitted, setSubmitted] = useState(false);

    const cancel = useCallback(() => props.onClose(false, undefined), [props.onClose]);

    const form = useForm({
        initialValues: {
            name: '',
            expirationDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
        },
        validate: {
            name: (value) => (value.length > 0 ? null : 'Name is required'),
        },
    });

    const handleSubmit = async (e: any) => {
        if (e) {
            e.preventDefault();
        }

        if (submitted) {
            return;
        }

        const validationResult = form.validate();
        if (!validationResult.hasErrors) {
            await createApiKey();
        }
    };

    const createApiKey = async () => {
        setSubmitted(true);
        setErrorMessage(<Text></Text>);
        setError(false);
        try {
            const { name, expirationDate } = form.values;
            const result = await postExternalApiKeyCreateApiKey({
                name,
                expiration: expirationDate?.toISOString() ?? null,
            });
            props.onClose(true, result);
        } catch (err: any) {
            console.error('Failed to create API key:', err);
            setError(true);
            setSubmitted(false);
            setErrorMessage(<Text>Failed to create API key. Please try again.</Text>);
        }
    };

    return (
        <>
            <Box p="m">
                <div>
                    <DatePicker
                        placeholder="Expiration Date"
                        label="Expiration Date"
                        name="expirationDate"
                        minDate={new Date()}
                        data-atid="ExpirationDateInput"
                        {...form.getInputProps('expirationDate')}
                    />
                    <Space h="md" />
                    <TextInput required data-atid="NameInput" label="Name" placeholder="Name" {...form.getInputProps('name')} />
                </div>
            </Box>
            <div hidden={!error}>
                <Box>
                    <Alert color="error">{errorMessage}</Alert>
                    <Space p="md" />
                </Box>
            </div>
            <Space h="md" />
            <Group position="right">
                <Button data-atid="SettingsCancelButton" onClick={cancel} variant="outline">
                    Cancel
                </Button>
                <Button data-atid="CreateAPIKeyButton" onClick={handleSubmit} variant={!submitted ? 'filled' : 'light'}>
                    {!submitted ? 'Create' : 'Creating keys...'}
                </Button>
            </Group>
        </>
    );
}
